import React from 'react';
import { useTranslation } from 'react-i18next';
import { Composition } from 'atomic-layout';
import styled from 'styled-components';

import { toHtml } from '@/component/customEditor/logic/serialization';
import { useProduct, usePurchaseProduct } from '@/store/learning-catalog';
import { ProviderAccessStatus, ProviderList } from '@/store/providers';
import { isRealm } from '@/utils/realms';
import { useProviderAccess } from '@/hooks/useProviderAccess';
import { ButtonList } from '@/components/Button/ButtonList';
import { Button } from '@/components/Button/Button';
import { LinkButton } from '@/components/Button/LinkButton';
import { useFeatureEnabled, FEATURE } from '@/feature-toggles';
import { BodyLg, Header1, Header3 } from '@/components/Typography/Typography';
import { ProviderLogo } from '@/components/ProviderLogo/ProviderLogo';
import { providerFormAreas, providerFormAreasLg } from './GeneralProvider';
import { PageLoader } from '@/components/Pageloader/Pageloader';

interface Props {
  accessProductId?: number;
}

enum PluralsightTitle {
  SEB = 'Pluralsight',
  ALL = 'Pluralsight',
}

const pluralsightTitle = isRealm('seb') ? PluralsightTitle.SEB : PluralsightTitle.ALL;

const SubheaderStyled = styled(Header3)`
  margin-top: 32px;
`;

export const Pluralsight = ({ accessProductId }: Props): JSX.Element => {
  const provider = ProviderList.PLURALSIGHT;
  const providerStatus = useProviderAccess(provider);
  const { product: productPluralsight, loading: loadingPluralsightProduct } = useProduct(accessProductId?.toString());
  const [purchasePluralsightProduct, { loading: loadingPurchasePluralsight }] = usePurchaseProduct(accessProductId);
  const { t } = useTranslation('providers');
  const isPluralsightFeatureEnabled = useFeatureEnabled(FEATURE.PROVIDER_PLURALSIGHT);

  const techRoomDescription = (() => {
    if (!productPluralsight) return '';

    try {
      return toHtml(productPluralsight.meta.DESCRIPTION);
    } catch (e) {
      console.error(e);
    }

    return productPluralsight.meta.DESCRIPTION || '';
  })();

  if (loadingPluralsightProduct) return <PageLoader />;

  const hasPluralsightAccess = providerStatus === ProviderAccessStatus.HAS_ACCESS;
  const hasPluralsightAccessPending = providerStatus === ProviderAccessStatus.PENDING_ACCESS;

  return (
    <Composition
      areas={providerFormAreas}
      areasLg={providerFormAreasLg}
      templateCols="minmax(0, 1fr)"
      templateColsLg="minmax(0, 9fr) minmax(0, 3fr)"
      gap={0.875}
      gapMd={1.75}
    >
      {({ First, Second }) => (
        <>
          <First>
            {isRealm('seb') ? (
              <Header1>Future-proof your tech skills – SEB Campus in partnership with Pluralsight</Header1>
            ) : (
              <Header1>
                {hasPluralsightAccess
                  ? t('pluralsight-welcome', { title: pluralsightTitle })
                  : t('Improve your digital awareness and technology skills with Pluralsight')}
              </Header1>
            )}
            {!hasPluralsightAccessPending && !hasPluralsightAccess && (
              <>
                {isRealm('seb') ? (
                  techRoomDescription && (
                    <BodyLg className="has-bottom-margin content" dangerouslySetInnerHTML={{ __html: techRoomDescription }} />
                  )
                ) : (
                  <BodyLg>Interested? Request for access to Pluralsight.</BodyLg>
                )}
              </>
            )}
            {hasPluralsightAccessPending && !hasPluralsightAccess && (
              <BodyLg>{t('pluralsight-awaiting-approval', { title: pluralsightTitle })}</BodyLg>
            )}
            {!hasPluralsightAccessPending && hasPluralsightAccess && isPluralsightFeatureEnabled && (
              <>
                {isRealm('seb') ? (
                  <>
                    <BodyLg>
                      “Technology and efficiency” is one of SEB’s group strategic priorities in the 2025-2027 business plan. By
                      enabling all SEB colleagues to learn and use emerging technologies, we foster innovation, creativity, and
                      performance. Below you can find <b>learning activities curated for SEB</b>. You can also choose to explore
                      the entire Pluralsight offering yourself.
                    </BodyLg>
                    <SubheaderStyled>Technical literacy</SubheaderStyled>
                    <BodyLg>
                      Gain conversational understanding of key technologies with Tech Foundations. Start by assessing your current
                      knowledge. Based on your result, move on to the course.
                    </BodyLg>
                    <SubheaderStyled>AI assistant and assessments</SubheaderStyled>
                    <BodyLg>
                      Get learning recommendations tailored to your needs by using Pluralsight’s AI assistant “Iris” and
                      assessments, e.g., Skill IQ and Role IQ.
                    </BodyLg>
                    <SubheaderStyled>Learn by doing</SubheaderStyled>
                    <BodyLg>
                      Learn by doing, using sandboxes and labs; real, live practice environments to test and explore code.
                    </BodyLg>
                  </>
                ) : (
                  <>
                    <BodyLg>{t('pluralsight-approved-p1')}</BodyLg>
                    <BodyLg>{t('pluralsight-approved-p2')}</BodyLg>
                  </>
                )}
              </>
            )}
          </First>
          <Second>
            <ButtonList align="right" padded="bottom">
              <ProviderLogo provider={provider} />
            </ButtonList>
            <ButtonList align="right">
              {accessProductId !== undefined && !hasPluralsightAccess && (
                <Button
                  $loading={loadingPurchasePluralsight}
                  $icon="check"
                  disabled={hasPluralsightAccessPending}
                  onClick={() => purchasePluralsightProduct({ variables: { products: [{ id: accessProductId }] } })}
                >
                  {hasPluralsightAccessPending ? t('access-requested') : t('request-access')}
                </Button>
              )}
              {hasPluralsightAccess && (
                <LinkButton
                  $type="primary"
                  href={productPluralsight ? productPluralsight.meta.CUSTOM_LINK || '' : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('pluralsight-go-to')}
                </LinkButton>
              )}
            </ButtonList>
          </Second>
        </>
      )}
    </Composition>
  );
};
